/*
 * side-menu.js
 * ------------------------------------------------------------
 * Click-to-open side navigation drawer.
 *
 * Modelled on cart-drawer.js: a fixed left drawer that traps focus,
 * locks body scroll, and restores focus to its trigger on close.
 * Relies on the global helpers in global.js:
 *   trapFocus(container, elementToFocus)
 *   removeTrapFocus(elementToFocus)
 *
 * Navigation model
 * ----------------
 * Every level of the menu is pre-rendered as sibling panels inside
 * .side-menu__panels:
 *   - [data-level="1"]                       always open (panel 1)
 *   - [data-level="2"][data-parent=handle]   one per primary item w/ children
 *   - [data-level="3"][data-parent=handle]   one per level-2 item w/ children
 * A category button carries data-target="<handle>"; its panel is the
 * one whose data-parent matches.
 *
 * State:
 *   this.activeElement  - trigger that opened the drawer (focus restore)
 *   this.openStack      - ordered [{ level, panel, button }] of open child
 *                         panels (max 2: a level-2, then a level-3)
 *   this.currentNavItem - the top-nav <a> currently underlined (.is-current)
 *
 * Hidden panels carry the `inert` attribute so Tab can never land inside
 * them; it is removed on open and re-added on collapse.
 */
class SideMenu extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('.side-menu__drawer');
    this.panels = this.querySelector('.side-menu__panels');
    this.panel1 = this.querySelector('[data-level="1"]');
    this.backdrop = this.querySelector('.side-menu__backdrop');
    this.closeButton = this.querySelector('.side-menu__close');
    this.leftHeader = document.getElementById('wallaceLeftHeader');
    this.sectionId = this.dataset.sectionId;

    this.openStack = [];
    this.currentNavItem = null;

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    if (this.backdrop) this.backdrop.addEventListener('click', this.close.bind(this));
    if (this.closeButton) this.closeButton.addEventListener('click', this.close.bind(this));

    this.bindPanelButtons();
    this.bindBackButtons();
    this.bindOpeners();

    // Below 990px only one panel is shown at a time (parents are hidden);
    // desktop shows them side by side. Reset panel state when the breakpoint is
    // crossed so a panel hidden in one mode never stays stuck in the other.
    this.mobileMql = window.matchMedia('(max-width: 989px)');
    this.onBreakpointChange = this.resetPanels.bind(this);
    this.mobileMql.addEventListener('change', this.onBreakpointChange);

    this.bindThemeEditorEvents();

    this.initNewsletterReturn();
  }

  // The opener delegation, the media-query listener and the theme-editor
  // listeners all live on objects that outlive this element (document / the
  // MediaQueryList). Without cleanup, every shopify:section:load in the theme
  // editor would leave another live copy behind, and clicks/events would then
  // be handled several times over — which is how the drawer ended up showing
  // more than one panel there. Remove them when the element is torn down.
  disconnectedCallback() {
    if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
    if (this.mobileMql && this.onBreakpointChange) {
      this.mobileMql.removeEventListener('change', this.onBreakpointChange);
    }
    this.unbindThemeEditorEvents();
  }

  // After a drawer newsletter submission, Shopify redirects back to
  // ...?customer_posted=true#SideMenuNewsletter (the form id becomes the URL
  // fragment). Reopen the drawer, bring the signup into view, and focus the
  // success/error message. A customer-form success is request-level, so the
  // footer signup also renders a message; since this only runs for the drawer's
  // own return (hash), suppress the footer's duplicate without affecting the
  // footer's own submissions (which return to #ContactFooter).
  initNewsletterReturn() {
    if (window.location.hash !== '#SideMenuNewsletter') return;

    ['ContactFooter-success', 'ContactFooter-error'].forEach((id) => {
      const footerMessage = document.getElementById(id);
      if (footerMessage) footerMessage.hidden = true;
    });

    const message =
      this.querySelector('#SideMenuNewsletter-success') ||
      this.querySelector('#SideMenuNewsletter-error');
    if (!message) return;

    this.open();
    // open() traps focus on the close button after the slide-in (transitionend
    // or a 300ms fallback); run just after so the message wins focus.
    setTimeout(() => {
      message.scrollIntoView({ block: 'center' });
      message.focus();
    }, 350);
  }

  isMobile() {
    return (this.mobileMql || window.matchMedia('(max-width: 989px)')).matches;
  }

  /* ---------------------------------------------------------- binding */
  bindPanelButtons() {
    this.querySelectorAll('.side-menu__item[data-target]').forEach((button) => {
      button.addEventListener('click', () => this.togglePanel(button));
    });
  }

  bindBackButtons() {
    this.querySelectorAll('.side-menu__back').forEach((button) => {
      button.addEventListener('click', () => {
        const panel = button.closest('.side-menu__panel');
        const level = parseInt(panel.dataset.level, 10);
        this.collapseTo(level - 1);
      });
    });
  }

  // Intercept plain left-clicks on any [data-side-menu-open] element (desktop
  // primary nav links + the mobile hamburger). Delegated on document so it
  // works no matter where the trigger sits (e.g. the hamburger lives in the
  // header section, not inside <side-menu>) and survives header re-renders.
  // Modifier / non-primary clicks fall through so the href still works.
  bindOpeners() {
    // Stored on the instance so disconnectedCallback can remove it (see there).
    this.onDocumentClick = (event) => {
      const opener = event.target.closest('[data-side-menu-open]');
      if (!opener) return;
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      this.open(opener);
    };
    document.addEventListener('click', this.onDocumentClick);
  }

  /* --------------------------------------------------- theme editor */
  // In the editor the drawer is otherwise invisible/inert, so nothing reveals
  // the block a merchant is editing and stale state can survive a re-render.
  // Wire the drawer to the editor lifecycle: selecting the section opens it,
  // selecting a block opens the drawer AND its panel (collapsing the rest), and
  // a section reload resets to a clean single-panel state.
  bindThemeEditorEvents() {
    if (!window.Shopify || !window.Shopify.designMode) return;

    this.onSectionLoad = (event) => {
      if (this.ownsEditorEvent(event)) this.resetPanels();
    };
    this.onSectionSelect = (event) => {
      if (this.ownsEditorEvent(event)) this.open();
    };
    this.onSectionDeselect = (event) => {
      if (this.ownsEditorEvent(event)) this.close();
    };
    this.onBlockSelect = (event) => {
      if (!this.contains(event.target)) return;
      this.open();
      this.revealPanelFor(event.target);
    };

    document.addEventListener('shopify:section:load', this.onSectionLoad);
    document.addEventListener('shopify:section:select', this.onSectionSelect);
    document.addEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.addEventListener('shopify:block:select', this.onBlockSelect);
  }

  unbindThemeEditorEvents() {
    if (!this.onSectionLoad) return;
    document.removeEventListener('shopify:section:load', this.onSectionLoad);
    document.removeEventListener('shopify:section:select', this.onSectionSelect);
    document.removeEventListener('shopify:section:deselect', this.onSectionDeselect);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
  }

  // Only react to our own section: match this section's id against the id the
  // editor reports. Selecting any other section (heroes, footer, …) reports a
  // different sectionId, so it never opens or closes this drawer.
  ownsEditorEvent(event) {
    return !!this.sectionId && !!event.detail && event.detail.sectionId === this.sectionId;
  }

  // Open the drawer to the panel that contains `el` (a just-selected block),
  // collapsing any others. Blocks in panel 1 need no child panel opened.
  revealPanelFor(el) {
    const panel = el.closest('.side-menu__panel');
    if (!panel || panel.dataset.level === '1') {
      this.collapseTo(0);
      return;
    }
    const button = this.querySelector(`.side-menu__item[data-target="${CSS.escape(panel.dataset.parent)}"]`);
    if (button) this.openPanel(button);
  }

  /* ------------------------------------------------------- resolution */
  panelForHandle(handle) {
    return this.querySelector(`.side-menu__panel[data-parent="${CSS.escape(handle)}"]`);
  }

  /* ------------------------------------------------------ open / close */
  togglePanel(button) {
    if (button.classList.contains('is-active')) {
      const panel = this.panelForHandle(button.dataset.target);
      const level = panel ? parseInt(panel.dataset.level, 10) : 2;
      this.collapseTo(level - 1);
    } else {
      this.openPanel(button);
    }
  }

  openPanel(button) {
    const handle = button.dataset.target;
    const panel = this.panelForHandle(handle);
    if (!panel) return;
    const level = parseInt(panel.dataset.level, 10);

    // Close any sibling/deeper panel first.
    this.collapseTo(level - 1);

    panel.hidden = false;
    panel.removeAttribute('inert');
    // Next frame so the slide-in transition runs from the initial state.
    requestAnimationFrame(() => panel.classList.add('is-open'));

    button.setAttribute('aria-expanded', 'true');
    button.classList.add('is-active');
    this.openStack.push({ level, panel, button });

    // Mobile: single-panel view. Hide the parent panel (panel 1 for a level-2,
    // the level-2 panel for a level-3) so the child renders in normal flow
    // instead of as an absolute overlay, which iOS Safari draws as blank.
    if (this.isMobile()) {
      const parent = button.closest('.side-menu__panel');
      if (parent) {
        parent.hidden = true;
        parent.setAttribute('inert', '');
      }
    }

    // data-target is now a path-unique key (e.g. p-apparel); the top-nav tie
    // still keys off the bare handle carried in data-handle.
    if (level === 2) this.setCurrentNav(button.dataset.handle || handle);

    // Move focus into the newly opened panel (its first link = "Shop all").
    const firstLink = panel.querySelector('a[href]');
    if (firstLink) firstLink.focus();
  }

  // Collapse every open panel deeper than `level` (0 = collapse all).
  collapseTo(level) {
    const mobile = this.isMobile();
    let focusTarget = null;

    while (this.openStack.length && this.openStack[this.openStack.length - 1].level > level) {
      const { panel, button } = this.openStack.pop();
      panel.classList.remove('is-open');
      panel.setAttribute('inert', ''); // block Tab immediately, before it finishes hiding
      button.setAttribute('aria-expanded', 'false');
      button.classList.remove('is-active');

      if (mobile) {
        // In-flow single-panel view: hide the child now and restore its parent
        // so exactly one panel is ever in normal flow (no overlap = no flash).
        panel.hidden = true;
        const parent = button.closest('.side-menu__panel');
        if (parent) {
          parent.hidden = false;
          parent.removeAttribute('inert');
        }
      } else {
        // Desktop keeps parents visible; animate the child out, then hide it.
        this.hidePanelAfterTransition(panel);
      }

      focusTarget = button; // the opener lives in the now-visible parent panel
    }

    if (level < 2) this.clearCurrentNav();

    // Return focus to the opener, but never while the drawer is closing.
    if (focusTarget && this.classList.contains('active')) focusTarget.focus();
  }

  // Breakpoint crossed (or defensive reset): return to panel 1 with consistent
  // hidden/inert state, since mobile hides parent panels while desktop shows
  // them side by side.
  resetPanels() {
    this.openStack = [];
    this.clearCurrentNav();
    this.querySelectorAll('.side-menu__item.is-active').forEach((button) => {
      button.classList.remove('is-active');
      button.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.side-menu__panel--child').forEach((panel) => {
      panel.classList.remove('is-open');
      panel.hidden = true;
      panel.setAttribute('inert', '');
    });
    if (this.panel1) {
      this.panel1.hidden = false;
      this.panel1.removeAttribute('inert');
    }
  }

  // Set `hidden` once the collapse transition ends (keeps it animating out),
  // unless the panel was re-opened in the meantime.
  hidePanelAfterTransition(panel) {
    const finalize = (event) => {
      if (event && event.target !== panel) return; // ignore bubbling child transitions
      panel.removeEventListener('transitionend', finalize);
      clearTimeout(timer);
      if (panel.classList.contains('is-open')) return; // re-opened; leave visible
      panel.hidden = true;
    };
    const timer = setTimeout(finalize, 260); // fallback for prefers-reduced-motion
    panel.addEventListener('transitionend', finalize);
  }

  /* ------------------------------------------------------- top-nav tie */
  setCurrentNav(handle) {
    this.clearCurrentNav();
    if (!this.leftHeader) return;
    const item = this.leftHeader.querySelector(`#HeaderMenu-${CSS.escape(handle)}`);
    if (item) {
      item.classList.add('is-current');
      this.currentNavItem = item;
    }
  }

  clearCurrentNav() {
    if (this.currentNavItem) {
      this.currentNavItem.classList.remove('is-current');
      this.currentNavItem = null;
    }
  }

  /* ----------------------------------------------------- drawer open */
  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    // Mutual exclusion: only one off-canvas drawer open at a time.
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && cartDrawer.classList.contains('active')) cartDrawer.close();

    requestAnimationFrame(() => this.classList.add('active'));

    // Trap focus once the drawer has slid in. transitionend won't fire under
    // prefers-reduced-motion, so a timeout fallback guarantees it.
    this.trapped = false;
    const trap = () => {
      if (this.trapped) return;
      this.trapped = true;
      trapFocus(this.drawer, this.closeButton);
      this.maybeDeepLink(triggeredBy);
    };
    this.addEventListener('transitionend', trap, { once: true });
    setTimeout(trap, 300);

    document.body.classList.add('overflow-hidden');
  }

  // Opening from a top-nav link (data-side-menu-handle) jumps straight to that
  // item's level-2 panel. Leaf handles just open the drawer at panel 1.
  maybeDeepLink(trigger) {
    const handle = trigger && trigger.dataset ? trigger.dataset.sideMenuHandle : null;
    if (!handle) return;
    // Top-nav links carry the bare handle; match it to the opener's data-handle
    // (its data-target is the path-unique key).
    const button = this.panel1.querySelector(`.side-menu__item[data-handle="${CSS.escape(handle)}"]`);
    if (button) this.openPanel(button);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    // Only release the shared scroll lock if no other drawer (e.g. the cart) is
    // still open, so closing one doesn't unlock the page behind another.
    if (!document.querySelector('side-menu.active, cart-drawer.active')) {
      document.body.classList.remove('overflow-hidden');
    }
    this.collapseTo(0); // reset every panel + clear nav underline
  }

  /* ------------------------------------------------------------- keys */
  onKeyUp(event) {
    if (!event.code || event.code.toUpperCase() !== 'ESCAPE') return;
    if (this.openStack.length) {
      // Escape peels one level at a time: level 3, then level 2, then close.
      const top = this.openStack[this.openStack.length - 1];
      this.collapseTo(top.level - 1);
    } else {
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('side-menu', SideMenu);
